import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import Swal from "sweetalert2";

const SignatureManagement = () => {
  const { api, showToast } = useAuth();
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    loadSignatures();
  }, [currentPage, search, statusFilter]);

  const loadSignatures = async () => {
    try {
      setLoading(true);
      const response = await api.signatures.getAll({
        page: currentPage,
        limit: 10,
        search: search,
        status: statusFilter,
      });

      if (response.success) {
        setSignatures(response.data);
        setTotalPages(response.pagination.pages);
      }
    } catch (error) {
      console.error("Error loading signatures:", error);
      showToast("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadSignatures();
  };

  const handleViewDetails = (signature) => {
    setSelectedSignature(signature);
    setShowModal(true);
  };

  const handleUpdateStatus = async (id, newStatus) => {
    const result = await Swal.fire({
      icon: "question",
      title: "ยืนยันการเปลี่ยนสถานะ",
      text: `ต้องการเปลี่ยนสถานะเป็น ${
        newStatus === "active" ? "แสดง" : "ซ่อน"
      } ใช่หรือไม่?`,
      showCancelButton: true,
      confirmButtonText: "ใช่, เปลี่ยนสถานะ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#007CC7",
    });

    if (result.isConfirmed) {
      try {
        const response = await api.signatures.updateStatus(id, newStatus);
        if (response.success) {
          showToast("เปลี่ยนสถานะเรียบร้อยแล้ว");
          loadSignatures();
        }
      } catch (error) {
        showToast("เกิดข้อผิดพลาดในการเปลี่ยนสถานะ", "error");
      }
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "ยืนยันการลบ",
      text: "คุณแน่ใจหรือไม่ที่จะลบข้อมูลนี้? การลบจะไม่สามารถกู้คืนได้",
      showCancelButton: true,
      confirmButtonText: "ใช่, ลบเลย",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc3545",
    });

    if (result.isConfirmed) {
      try {
        const response = await api.signatures.delete(id);
        if (response.success) {
          showToast("ลบข้อมูลเรียบร้อยแล้ว");
          loadSignatures();
        }
      } catch (error) {
        showToast("เกิดข้อผิดพลาดในการลบข้อมูล", "error");
      }
    }
  };

  return (
    <div className="signature-management py-4">
      <div className="container-fluid">
        <div className="mb-4">
          <h1 className="h3 mb-0">
            <i className="fas fa-signature me-2"></i>
            จัดการลายเซ็น
          </h1>
        </div>

        {/* Search and Filter */}
        <div className="card mb-4">
          <div className="card-body">
            <form onSubmit={handleSearch}>
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label">ค้นหา</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="ค้นหาชื่อ, หน่วยงาน, ข้อความ..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">สถานะ</label>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="active">แสดง</option>
                    <option value="hidden">ซ่อน</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <button type="submit" className="btn btn-gradient-primary">
                    <i className="fas fa-search me-2"></i>
                    ค้นหา
                  </button>
                </div>
                <div className="col-md-3 text-end">
                  <button
                    type="button"
                    className="btn btn-gradient-success"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("active");
                      setCurrentPage(1);
                    }}
                  >
                    <i className="fas fa-sync me-2"></i>
                    รีเซ็ต
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-header card-header-gradient">
            <h5 className="mb-0">รายการลงนาม</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th width="5%">#</th>
                      <th width="15%">ชื่อ-นามสกุล</th>
                      <th width="15%">หน่วยงาน</th>
                      <th width="10%">จังหวัด</th>
                      <th width="25%">ข้อความ</th>
                      <th width="10%">สถานะ</th>
                      <th width="10%">วันที่</th>
                      <th width="10%">การจัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signatures.map((signature, index) => (
                      <tr key={signature.id}>
                        <td>{(currentPage - 1) * 10 + index + 1}</td>
                        <td>{signature.fullname}</td>
                        <td>{signature.organization || "-"}</td>
                        <td>{signature.province || "-"}</td>
                        <td>
                          <span
                            className="text-truncate d-inline-block"
                            style={{ maxWidth: "300px" }}
                            title={signature.message}
                          >
                            {signature.message}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              signature.status === "active"
                                ? "bg-success"
                                : "bg-secondary"
                            }`}
                          >
                            {signature.status === "active" ? "แสดง" : "ซ่อน"}
                          </span>
                        </td>
                        <td>
                          {new Date(signature.created_at).toLocaleDateString(
                            "th-TH"
                          )}
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-info"
                              title="ดูรายละเอียด"
                              onClick={() => handleViewDetails(signature)}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className={`btn ${
                                signature.status === "active"
                                  ? "btn-warning"
                                  : "btn-success"
                              }`}
                              title={
                                signature.status === "active" ? "ซ่อน" : "แสดง"
                              }
                              onClick={() =>
                                handleUpdateStatus(
                                  signature.id,
                                  signature.status === "active"
                                    ? "hidden"
                                    : "active"
                                )
                              }
                            >
                              <i
                                className={`fas fa-${
                                  signature.status === "active"
                                    ? "eye-slash"
                                    : "eye"
                                }`}
                              ></i>
                            </button>
                            <button
                              className="btn btn-danger"
                              title="ลบ"
                              onClick={() => handleDelete(signature.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {signatures.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center text-muted py-4">
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="mt-4">
                <ul className="pagination justify-content-center">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ก่อนหน้า
                    </button>
                  </li>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <li
                        key={pageNum}
                        className={`page-item ${
                          currentPage === pageNum ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  {totalPages > 5 && (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                  <li
                    className={`page-item ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      ถัดไป
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>

        {/* Detail Modal */}
        {showModal && selectedSignature && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-info-circle me-2"></i>
                    รายละเอียดการลงนาม
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="fw-bold">ชื่อ-นามสกุล:</label>
                      <p>{selectedSignature.fullname}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-bold">หน่วยงาน:</label>
                      <p>{selectedSignature.organization || "-"}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="fw-bold">จังหวัด:</label>
                      <p>{selectedSignature.province || "-"}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-bold">IP Address:</label>
                      <p>{selectedSignature.ip_address || "-"}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12">
                      <label className="fw-bold">ข้อความถวายพระพร:</label>
                      <p className="bg-light p-3 rounded">
                        {selectedSignature.message}
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <label className="fw-bold">สถานะ:</label>
                      <p>
                        <span
                          className={`badge ${
                            selectedSignature.status === "active"
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                        >
                          {selectedSignature.status === "active"
                            ? "แสดง"
                            : "ซ่อน"}
                        </span>
                      </p>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-bold">วันที่ลงนาม:</label>
                      <p>
                        {new Date(
                          selectedSignature.created_at
                        ).toLocaleDateString("th-TH", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    ปิด
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignatureManagement;
